import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import _ from 'lodash';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Get the active booked device IDs based on current time and bookings
export const getActiveBookedDeviceIDs = (bookings) => {
	const now = new Date();

	return bookings
		.filter(
			(b) =>
				b.startTime && b.endTime && new Date(b.startTime) <= now && new Date(b.endTime) >= now && !b.deletedDate
		)
		.map((b) => b.deviceID);
};

// Format a number with leading zero if it's less than 10
// Returns '0' for 0, '01' for 1, '10'
export const formatWithLeadingZero = (number) => {
	if (number === 0) {
		return '0';
	}
	return String(number).padStart(2, '0');
};

// Define color options for each segment
export const segmentedOptions = {
	'Availability': [
		{ text: 'Booked', description: 'Lockers currently booked', color: '#A85EFF' },
		{ text: 'Available', description: 'Lockers available for booking', color: '#FFC107' }
	],
	'Maintenance': [
		{ text: 'Broken Door', description: 'Locker door needs repair', color: '#A85EFF' },
		{ text: 'Jammed Lockers', description: 'Lockers cannot be opened or closed properly', color: '#FF7043' },
		{ text: 'Battery Issues', description: 'Locker needs battery changing', color: '#00C9A7' },
		{ text: 'Card Reader Fail', description: 'Locker unable to read card', color: '#FFC107' }
	],
	'Campers': [
		{ text: 'Camped', description: 'Booked lockers that have not been accessed in long time', color: '#00C9A7' },
		{ text: 'Active', description: 'Lockers accessed regularly', color: '#D9D9D9' }
	],
	'Locker Usage': [
		{ text: 'High', description: '60-100 quarterly', color: '#6A0DAD' },
		{ text: 'Medium', description: '40-60 quarterly', color: '#A85EFF' },
		{ text: 'Low', description: '10-40 quarterly', color: '#E0B3FF' },
		{ text: 'None', description: '0 quarterly', color: '#D9D9D9' }
	],
	'Sizing': [
		{ text: 'Extra Large', description: 'Size Xl', color: '#FF7043' },
		{ text: 'Large', description: 'Size L', color: '#A85EFF' },
		{ text: 'Medium', description: 'Size M', color: '#00C9A7' },
		{ text: 'Small', description: 'Size S', color: '#FFC107' }
	],
	'Battery': [
		{ text: 'High', description: '75%-100%', color: '#A85EFF' },
		{ text: 'Medium', description: '30%-75%', color: '#00C9A7' },
		{ text: 'Low', description: '15%-30%', color: '#FFC107' },
		{ text: 'Critical', description: '1%-14%', color: '#FF7043' },
		{ text: 'Dead', description: '0%', color: '#D9D9D9' }
	]
};

//Determine tooltip text based on selected segment and device data
export const getTooltipText = ({ selectedSegment, device, bookings, maintenance, logs, people, maintenanceTypes }) => {
	switch (selectedSegment) {
		case 'Availability':
			return getAvailabilityTooltip(device, bookings, people);

		case 'Maintenance':
			return getMaintenanceTooltip(device, maintenance, maintenanceTypes);

		case 'Campers':
			return getCampersTooltip(device, logs);

		case 'Locker Usage':
			return getLockerUsageTooltip(device, logs);

		case 'Sizing':
			return getSizingTooltip(device);

		case 'Battery':
			return getBatteryTooltip(device);

		default:
			return device.name;
	}
};

// Get the availability tooltip text for a device
const getAvailabilityTooltip = (device, bookings, people) => {
	const booking = bookings.find((b) => b.deviceID === device.deviceID);
	if (!booking) return 'Locker is available';

	const person = people.find((p) => p.personID === booking.assignedToPerson)?.displayName || '';
	const start = dayjs(booking.startTime).format('D MMMM');
	const end = dayjs(booking.endTime).format('D MMMM');
	const startHour = dayjs(booking.startTime).format('HH:mm A');
	const endHour = dayjs(booking.endTime).format('HH:mm A');

	return (
		<>
			{person}
			<br />
			{start} - {end}
			<br />
			{startHour} - {endHour}
		</>
	);
};

// Get the maintenance tooltip text for a device
const getMaintenanceTooltip = (device, maintenance, maintenanceTypes) => {
	const mLog = maintenance.find((m) => m.assetID === device.deviceID);

	if (!mLog) return 'No maintenance reported';

	const type = maintenanceTypes.find((t) => t.maintenanceTypeID === mLog.maintenanceTypeID);
	return `${type?.name || 'Unknown'} - Reported ${dayjs(mLog.createdDate).format('MMMM D, YYYY')}`;
};

// Get the campers tooltip text for a device
const getCampersTooltip = (device, logs) => {
	const lockMac = device.fields?.find((f) => f.fieldTitle === 'Lock Mac')?.value;
	if (!lockMac) return 'No access data available';

	const recentLog = logs.find((l) => l.lockMac === lockMac);
	if (!recentLog) return 'No access recorded';

	const daysSince = dayjs().diff(dayjs(recentLog.createdDate), 'day');

	if (daysSince === 0) return 'Last accessed today';
	if (daysSince === 1) return 'Last accessed yesterday';
	if (daysSince > 30) return `No access in ${daysSince} days`;

	return `Last accessed ${daysSince} days ago`;
};

// Get the locker usage tooltip text for a device
const getLockerUsageTooltip = (device, logs) => {
	const lockMac = device.fields?.find((f) => f.fieldTitle === 'Lock Mac')?.value;
	if (!lockMac) return 'No usage data available';

	const usageCount = logs.filter((l) => l.lockMac === lockMac).length;
	return `Unlocked ${usageCount} ${usageCount === 1 ? 'time' : 'times'} over the last 3 months`;
};

// Get the locker usage count for a specific device
export const getLockerUsageCount = (device, logs) => {
	const lockMac = device.fields?.find((f) => f.fieldTitle === 'Lock Mac')?.value;
	if (!lockMac) return 0;

	return logs.filter((l) => l.lockMac === lockMac).length;
};

// Get the locker usage category (High, Medium, Low, None) for a specific device
export const getLockerUsageCategory = (device, logs) => {
	const usageCount = getLockerUsageCount(device, logs);
	const usageOptions = segmentedOptions['Locker Usage'];

	if (usageCount === 0) {
		return usageOptions.find((opt) => opt.text === 'None')?.text || 'None';
	} else if (usageCount >= 40) {
		return usageOptions.find((opt) => opt.text === 'High')?.text || 'High';
	} else if (usageCount >= 10) {
		return usageOptions.find((opt) => opt.text === 'Medium')?.text || 'Medium';
	} else if (usageCount >= 1) {
		return usageOptions.find((opt) => opt.text === 'Low')?.text || 'Low';
	}
	return usageOptions.find((opt) => opt.text === 'None')?.text || 'None';
};

// Get the size category for a specific device
export const getDeviceSizeCategory = (device) => {
	const sizeValue = device?.fields?.find((field) => field.fieldTitle === 'Size' && !field.fieldDeletedDate)?.value;

	if (!sizeValue) return '-';

	const sizingOptions = segmentedOptions['Sizing'];
	const foundOption = sizingOptions.find((opt) => opt.text === sizeValue);

	return foundOption?.text || sizeValue;
};

// Get the sizing tooltip text for a device
const getSizingTooltip = (device) => {
	const sizeValue = device.fields?.find((f) => f.fieldTitle === 'Size')?.value;
	return sizeValue ? `Size: ${sizeValue}` : 'Size not specified';
};

// Get the battery tooltip text for a device
const getBatteryTooltip = (device) => {
	const battery = device.battery;
	return isNaN(battery) ? 'No battery info' : `Battery: ${battery}%`;
};

// Determine background color based on device status and selected segment
export const getBackgroundColor = ({ device, bookings, maintenance, logs, selectedSegment, maintenanceTypes }) => {
	switch (selectedSegment) {
		case 'Availability':
			return getAvailabilityColor(device, bookings);

		case 'Maintenance':
			return getMaintenanceColor(device, maintenance, maintenanceTypes);

		case 'Campers':
			return getCampersColor(device, logs);

		case 'Locker Usage':
			return getLockerUsageColor(device, logs);

		case 'Sizing':
			return getSizingColor(device);

		case 'Battery':
			return getBatteryColor(device);

		default:
			return '#D9D9D9';
	}
};

// Get the color for the device based on its availability status
export const getAvailabilityColor = (device, bookings) => {
	const availabilityOptions = segmentedOptions['Availability'];
	const activeBookedDeviceIDs = getActiveBookedDeviceIDs(bookings);

	const isBooked = activeBookedDeviceIDs.includes(device.deviceID);

	return isBooked
		? availabilityOptions.find((opt) => opt.text === 'Booked')?.color
		: availabilityOptions.find((opt) => opt.text === 'Available')?.color;
};

// Get the color for the device based on its maintenance status
export const getMaintenanceColor = (device, maintenanceLogs, maintenanceTypes) => {
	const maintenanceOptions = segmentedOptions['Maintenance'];

	const activeLog = maintenanceLogs.find(
		(log) => log.assetID === device.deviceID && !log.resolvedDate && !log.deletedDate
	);

	if (!activeLog) {
		return maintenanceOptions.find((opt) => opt.text === 'None')?.color || '#D9D9D9';
	}

	const typeRecord = maintenanceTypes.find((t) => t.maintenanceTypeID === activeLog.maintenanceTypeID);

	const name = typeRecord?.userFriendlyName?.toLowerCase() || '';

	if (name.includes('broken')) {
		return maintenanceOptions.find((opt) => opt.text === 'Broken Door')?.color;
	}
	if (name.includes('jammed')) {
		return maintenanceOptions.find((opt) => opt.text === 'Jammed Lockers')?.color;
	}
	if (name.includes('battery')) {
		return maintenanceOptions.find((opt) => opt.text === 'Battery Issues')?.color;
	}
	if (name.includes('card reader')) {
		return maintenanceOptions.find((opt) => opt.text === 'Card Reader Fail')?.color;
	}
	return '#D9D9D9'; // fallback
};

// Get the color for the device based on its camping status
export const getCampersColor = (device, logs) => {
	const camperOptions = segmentedOptions['Campers'];

	const lockMac = device.fields?.find((f) => f.fieldTitle === 'Lock Mac' && !f.fieldDeletedDate)?.value;

	if (!lockMac) return camperOptions.find((opt) => opt.text === 'Active')?.color;

	const thirtyDaysAgo = new Date();
	thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

	const hasRecentAccess = logs.some((log) => log.lockMac === lockMac && new Date(log.createdDate) >= thirtyDaysAgo);

	return hasRecentAccess
		? camperOptions.find((opt) => opt.text === 'Active')?.color
		: camperOptions.find((opt) => opt.text === 'Camped')?.color;
};

// Get the color for the device based on its locker usage
export const getLockerUsageColor = (device, logs) => {
	const usageOptions = segmentedOptions['Locker Usage'];

	const lockMacUsageMap = logs.reduce((acc, log) => {
		if (!log.lockMac) return acc;
		acc[log.lockMac] = (acc[log.lockMac] || 0) + 1;
		return acc;
	}, {});

	const lockMac = device.fields?.find((f) => f.fieldTitle === 'Lock Mac' && !f.fieldDeletedDate)?.value;

	if (!lockMac) return usageOptions.find((opt) => opt.text === 'None')?.color;

	const usageCount = lockMacUsageMap[lockMac] || 0;

	if (usageCount === 0) {
		return usageOptions.find((opt) => opt.text === 'None')?.color;
	} else if (usageCount >= 60) {
		return usageOptions.find((opt) => opt.text === 'High')?.color;
	} else if (usageCount >= 40) {
		return usageOptions.find((opt) => opt.text === 'Medium')?.color;
	} else if (usageCount >= 10) {
		return usageOptions.find((opt) => opt.text === 'Low')?.color;
	}

	// fallback
	return usageOptions.find((opt) => opt.text === 'None')?.color;
};

// Get the color for the device based on its sizing
export const getSizingColor = (device) => {
	const sizeValue = device.fields?.find((field) => field.fieldTitle === 'Size' && !field.fieldDeletedDate)?.value;

	const sizingOptions = segmentedOptions['Sizing'];

	return (
		sizingOptions.find((opt) => opt.text === sizeValue)?.color || '#D9D9D9' // fallback
	);
};

// Get the color for the device based on its battery status
export const getBatteryColor = (device) => {
	const batteryValue = device.battery;
	const batteryOptions = segmentedOptions['Battery'];

	if (!(batteryValue > 0)) {
		return batteryOptions.find((opt) => opt.text === 'Dead')?.color;
	}
	if (batteryValue > 0 && batteryValue < 15) {
		return batteryOptions.find((opt) => opt.text === 'Critical')?.color;
	}
	if (batteryValue > 14 && batteryValue < 30) {
		return batteryOptions.find((opt) => opt.text === 'Low')?.color;
	}
	if (batteryValue > 29 && batteryValue < 75) {
		return batteryOptions.find((opt) => opt.text === 'Medium')?.color;
	}
	if (batteryValue > 74) {
		return batteryOptions.find((opt) => opt.text === 'High')?.color;
	}
	// fallback por si todo falla
	return batteryOptions.find((opt) => opt.text === 'Dead')?.color;
};

// Get weekly activity data for the last 7 days (Saturday to Friday)
export const getWeeklyActivityData = (logs = []) => {
	const dayCounts = new Array(7).fill(0); // Saturday to Friday

	const today = dayjs().startOf('day');
	const startOfWeek = dayjs().startOf('week').subtract(1, 'day').startOf('day'); // Saturday

	logs.forEach((log) => {
		const logDate = dayjs(log.createdDate).startOf('day');

		if (logDate.isSameOrAfter(startOfWeek) && logDate.isSameOrBefore(today)) {
			const dayIndex = (logDate.day() + 1) % 7; // Adjust: Saturday = 6
			dayCounts[dayIndex]++;
		}
	});

	const shortDayNames = ['S', 'S', 'M', 'T', 'W', 'T', 'F'];
	const fullDayNames = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

	const dayActivityData = dayCounts.map((count, index) => ({
		name: shortDayNames[index],
		value: count
	}));

	const maxDayIndex = dayCounts.indexOf(Math.max(...dayCounts));
	const maxActivityDay = fullDayNames[maxDayIndex];

	return { maxActivityDay, dayActivityData };
};

// Get locker utilization data based on logs and selected time frame (weekly, monthly, or quarterly)
export const getLockerUtilizationData = (logs = [], timeFrame = '1') => {
	const now = dayjs();
	const threeMonthsAgo = now.subtract(3, 'month').startOf('day');

	// Filter logs to only those in the last 6 months if needed for quarterly comparison
	const sixMonthsAgo = now.subtract(6, 'month').startOf('day');
	const filteredLogs =
		timeFrame === '3'
			? logs.filter((log) => dayjs(log.createdDate).isSameOrAfter(sixMonthsAgo))
			: logs.filter((log) => dayjs(log.createdDate).isSameOrAfter(threeMonthsAgo));

	// Build weekly
	if (timeFrame === '1') {
		const weeks = [];

		for (let i = 0; i < 13; i++) {
			const start = dayjs()
				.subtract(12 - i, 'week')
				.startOf('week');
			const end = start.endOf('week');

			const weekLogs = filteredLogs.filter((log) => {
				const logDate = dayjs(log.createdDate);
				return logDate.isSameOrAfter(start) && logDate.isSameOrBefore(end);
			});

			weeks.push({
				name: `W${i + 1}`,
				value: weekLogs.length
			});
		}

		return weeks;
	}

	// Build monthly
	if (timeFrame === '2') {
		const months = [];

		for (let i = 0; i < 3; i++) {
			const start = now.subtract(2 - i, 'month').startOf('month');
			const end = start.endOf('month');

			const monthLogs = filteredLogs.filter((log) => {
				const logDate = dayjs(log.createdDate);
				return logDate.isSameOrAfter(start) && logDate.isSameOrBefore(end);
			});

			months.push({
				name: start.format('MMM'), // 'Jul', 'Aug', etc.
				value: monthLogs.length
			});
		}

		return months;
	}

	// Build quarterly: compare current 3 months vs previous 3 months
	if (timeFrame === '3') {
		const currentTrimesterStart = now.subtract(3, 'month').startOf('day');
		const previousTrimesterStart = now.subtract(6, 'month').startOf('day');
		const previousTrimesterEnd = currentTrimesterStart.subtract(1, 'day');

		const currentLogs = filteredLogs.filter((log) => dayjs(log.createdDate).isSameOrAfter(currentTrimesterStart));

		const previousLogs = filteredLogs.filter((log) => {
			const logDate = dayjs(log.createdDate);
			return logDate.isSameOrAfter(previousTrimesterStart) && logDate.isSameOrBefore(previousTrimesterEnd);
		});

		return [
			{ name: 'Prev Trimester', value: previousLogs.length },
			{ name: 'Current Trimester', value: currentLogs.length }
		];
	}

	return [];
};

// Calculate the percentage increase in usage between two time frames (weekly, monthly, or quarterly)
export const getUsageIncreasePercentage = (logs = [], timeFrame = '1') => {
	const now = dayjs();

	let currentStart, currentEnd, previousStart, previousEnd;

	if (timeFrame === '1') {
		// Weekly
		currentStart = now.startOf('week');
		currentEnd = now.endOf('week');
		previousStart = currentStart.subtract(1, 'week');
		previousEnd = currentStart.subtract(1, 'day').endOf('day');
	} else if (timeFrame === '2') {
		// Monthly
		currentStart = now.startOf('month');
		currentEnd = now.endOf('month');
		previousStart = currentStart.subtract(1, 'month');
		previousEnd = currentStart.subtract(1, 'day').endOf('day');
	} else if (timeFrame === '3') {
		// Trimester (3 months)
		currentStart = now.subtract(3, 'month').startOf('day');
		currentEnd = now.endOf('day');
		previousStart = now.subtract(6, 'month').startOf('day');
		previousEnd = currentStart.subtract(1, 'day').endOf('day');
	}

	const currentLogs = logs.filter((log) => {
		const date = dayjs(log.createdDate);
		return date.isSameOrAfter(currentStart) && date.isSameOrBefore(currentEnd);
	});

	const previousLogs = logs.filter((log) => {
		const date = dayjs(log.createdDate);
		return date.isSameOrAfter(previousStart) && date.isSameOrBefore(previousEnd);
	});

	const currentCount = currentLogs.length;
	const previousCount = previousLogs.length;

	if (previousCount === 0 && currentCount === 0) return 0;
	if (previousCount === 0 && currentCount > 0) return 100;

	const increase = ((currentCount - previousCount) / previousCount) * 100;

	return Math.min(Number(increase.toFixed(0)), 100);
};

// Get locker metrics grouped by category(booked lockers and available lockers for each category)
export const getLockerMetricsGroupedByCategory = ({ devices, bookings, categories }) => {
	const now = new Date();

	const activeBookedDeviceIDs = bookings
		.filter(
			(b) =>
				b.startTime && b.endTime && new Date(b.startTime) <= now && new Date(b.endTime) >= now && !b.deletedDate
		)
		.map((b) => b.deviceID);

	return categories.map((category) => {
		const categoryID = category.categoryID;

		const categoryDevices = devices.filter((d) => d.categoryID === categoryID);
		const bookedCount = categoryDevices.filter((d) => activeBookedDeviceIDs.includes(d.deviceID)).length;
		const availableCount = categoryDevices.length - bookedCount;

		return {
			categoryID,
			categoryName: category.name,
			booked: bookedCount,
			available: availableCount,
			total: categoryDevices.length
		};
	});
};

// Get locker metrics (booked, available, and maintenance counts)
export const getLockerMetrics = ({ devices = [], bookings = [], maintenanceLogs = [] }) => {
	if (!devices.length) {
		return {
			bookedCount: 0,
			availableCount: 0,
			maintenanceCount: 0
		};
	}

	const activeBookingDeviceIDs = Array.from(new Set(getActiveBookedDeviceIDs(bookings)));
	const maintenanceDeviceIDs = maintenanceLogs.map((m) => m.assetID);

	const bookedCount = devices.filter((d) => activeBookingDeviceIDs.includes(d.deviceID)).length;
	const maintenanceCount = devices.filter((d) => maintenanceDeviceIDs.includes(d.deviceID)).length;
	const availableCount = devices.length - bookedCount - maintenanceCount;

	return {
		bookedCount,
		maintenanceCount,
		availableCount
	};
};

// Sort lockers naturally by name, handling alphanumeric and special characters
export const sortLockersNaturalWay = (items) => {
	if (!items || items.length === 0) {
		return [];
	}
	try {
		return _.orderBy(
			items,
			[
				// Extract and sort alphabetic segments, if present
				(item) => {
					const alphaMatch = item?.name.match(/[A-Za-z]+/);
					return alphaMatch ? alphaMatch[0].trim().toUpperCase() : ''; // Normalize and handle missing alphabetic part
				},
				// Extract and sort numeric segments, if present
				(item) => {
					const numericMatch = item?.name.match(/\d+/);
					return numericMatch ? parseInt(numericMatch[0], 10) : Number.MAX_SAFE_INTEGER; // Handle missing numeric part
				},
				// Handle special characters like hyphens, spaces or dots, to give preference to similar names with separators
				(item) => {
					const specialMatch = item?.name.match(/[-. ]/);
					return specialMatch ? specialMatch[0] : ''; // Sort based on special characters, if present
				}
			],
			['asc', 'asc', 'asc']
		);
	} catch (error) {
		return items; // Return unsorted items in case of error
	}
};
